import { useRef, useEffect, useCallback, useState } from "react";

/**
 * Hook that synchronizes vertical scroll between two panels using proportional offset.
 * When enabled, scrolling one panel proportionally scrolls the other.
 * Uses callback refs so sync re-attaches when DOM elements remount (e.g. mode switch).
 *
 * @param enabled - Whether synchronized scrolling is active
 * @returns refs to attach to the before and after scroll containers
 */
export function useSyncScroll(enabled: boolean) {
  const [beforeEl, setBeforeEl] = useState<HTMLDivElement | null>(null);
  const [afterEl, setAfterEl] = useState<HTMLDivElement | null>(null);
  const isSyncing = useRef(false);
  /** When > 0, sync scroll handlers are paused (for programmatic navigation). */
  const pauseCount = useRef(0);

  const beforeRef = useCallback((node: HTMLDivElement | null) => { setBeforeEl(node); }, []);
  const afterRef = useCallback((node: HTMLDivElement | null) => { setAfterEl(node); }, []);

  /** Temporarily pause sync scroll. Returns a resume function. Auto-resumes after `ms`. */
  const pauseSync = useCallback((ms = 600) => {
    pauseCount.current++;
    let resumed = false;
    const resume = () => {
      if (resumed) return;
      resumed = true;
      pauseCount.current = Math.max(0, pauseCount.current - 1);
    };
    setTimeout(resume, ms);
    return resume;
  }, []);

  useEffect(() => {
    if (!enabled || !beforeEl || !afterEl) return;

    function computeRatio(el: HTMLElement): number {
      const maxScroll = el.scrollHeight - el.clientHeight;
      if (maxScroll <= 0) return 0;
      return el.scrollTop / maxScroll;
    }

    function syncTarget(target: HTMLElement, ratio: number) {
      const maxScroll = target.scrollHeight - target.clientHeight;
      if (maxScroll <= 0) return;
      target.scrollTop = ratio * maxScroll;
    }

    function handleBeforeScroll() {
      if (isSyncing.current || pauseCount.current > 0) return;
      isSyncing.current = true;
      const ratio = computeRatio(beforeEl!);
      syncTarget(afterEl!, ratio);
      isSyncing.current = false;
    }

    function handleAfterScroll() {
      if (isSyncing.current || pauseCount.current > 0) return;
      isSyncing.current = true;
      const ratio = computeRatio(afterEl!);
      syncTarget(beforeEl!, ratio);
      isSyncing.current = false;
    }

    beforeEl.addEventListener("scroll", handleBeforeScroll);
    afterEl.addEventListener("scroll", handleAfterScroll);

    return () => {
      beforeEl.removeEventListener("scroll", handleBeforeScroll);
      afterEl.removeEventListener("scroll", handleAfterScroll);
    };
  }, [enabled, beforeEl, afterEl]);

  return { beforeRef, afterRef, pauseSync };
}
